import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Paper } from '../sales/print/usePrint';

/**
 * Turns a print layout (the same A4 document or 80 mm receipt the Print button uses) into a PDF to
 * attach on WhatsApp: the node is painted off-screen inside the app (so it sees the shop's data
 * and logo), captured as an image (html2canvas) and paged into a PDF (jsPDF). Both libraries load
 * only when a PDF is made.
 */

interface Job {
  node: ReactNode;
  paper: Paper;
  resolve: (base64: string) => void;
  reject: (error: unknown) => void;
}

const WIDTH: Record<Paper, string> = { a4: '210mm', 'a4-landscape': '297mm', '80mm': '80mm' };
const PADDING: Record<Paper, string> = { a4: '12mm', 'a4-landscape': '10mm 12mm', '80mm': '0' };

const frame = () => new Promise((r) => requestAnimationFrame(r));

export function usePdf() {
  const [job, setJob] = useState<Job | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!job) return;
    let done = false;
    const run = async () => {
      await document.fonts.ready;
      await frame();
      await frame();
      const el = ref.current;
      if (!el) throw new Error('Nothing to make a PDF from.');
      const base64 = await capture(el, job.paper);
      done = true;
      job.resolve(base64);
    };
    run()
      .catch((e: unknown) => {
        if (!done) job.reject(e);
      })
      .finally(() => setJob(null));
  }, [job]);

  const make = useCallback(
    (paper: Paper, node: ReactNode) =>
      new Promise<string>((resolve, reject) => setJob({ node, paper, resolve, reject })),
    [],
  );
  const element = job
    ? createPortal(
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: -20000,
            top: 0,
            width: WIDTH[job.paper],
            padding: PADDING[job.paper],
            boxSizing: 'border-box',
            background: '#fff',
            color: '#101828',
          }}
        >
          <div ref={ref}>{job.node}</div>
        </div>,
        document.body,
      )
    : null;
  return { make, element, making: job !== null };
}

async function capture(el: HTMLElement, paper: Paper): Promise<string> {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas-pro'),
    import('jspdf'),
  ]);
  const target = el.parentElement ?? el;
  const canvas = await html2canvas(target, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
  if (paper === '80mm') {
    const height = (canvas.height * 80) / canvas.width;
    const pdf = new jsPDF({ unit: 'mm', format: [80, Math.max(height, 40)] });
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 80, height);
    return base64Of(pdf.output('datauristring'));
  }
  const landscape = paper === 'a4-landscape';
  const [pageW, pageH] = landscape ? [297, 210] : [210, 297];
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: landscape ? 'landscape' : 'portrait',
  });
  // One page's worth of canvas pixels; long documents continue on the next page.
  const slice = Math.floor((canvas.width * pageH) / pageW);
  const page = document.createElement('canvas');
  page.width = canvas.width;
  for (let y = 0, n = 0; y < canvas.height; y += slice, n += 1) {
    const h = Math.min(slice, canvas.height - y);
    page.height = h;
    const ctx = page.getContext('2d');
    if (!ctx) throw new Error('Canvas is not available.');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, page.width, h);
    ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
    if (n > 0) pdf.addPage();
    pdf.addImage(
      page.toDataURL('image/jpeg', 0.92),
      'JPEG',
      0,
      0,
      pageW,
      (h * pageW) / canvas.width,
    );
  }
  return base64Of(pdf.output('datauristring'));
}

function base64Of(dataUri: string): string {
  return dataUri.slice(dataUri.indexOf(',') + 1);
}
